import { useState, useEffect } from "react"

type WindowSizeState = {
  width: number | null
  height: number | null
  isMobile: boolean
}

/**
 * Hook that returns the current window size and whether it's mobile
 * @param {number} [mobileBreakpoint=768] - The breakpoint for determining mobile vs desktop
 * @returns {Object} { width, height, isMobile } - An object containing the window size and whether it's mobile
 */
export default function useWindowSize(mobileBreakpoint: number = 768): WindowSizeState {
  const isSSR = typeof window === "undefined"

  const [windowSize, setWindowSize] = useState<WindowSizeState>({
    width: isSSR ? null : window.innerWidth,
    height: isSSR ? null : window.innerHeight,
    isMobile: isSSR ? false : window.innerWidth < mobileBreakpoint
  })

  function changeWindowSize() {
    if (typeof window === "undefined") return
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
      isMobile: window.innerWidth < mobileBreakpoint
    })
  }

  useEffect(() => {
    if (isSSR) return

    window.addEventListener("resize", changeWindowSize)

    return () => {
      window.removeEventListener("resize", changeWindowSize)
    }
  }, [mobileBreakpoint, isSSR])

  return windowSize
}
